/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useEffect } from "react";
import { CustomTextField } from "./SellAiStyledComponents";
import { Grid, Typography, TextField, FormControl, Select, MenuItem, CircularProgress, Checkbox, Chip, useMediaQuery } from "@material-ui/core";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import { publicApi } from "../../../services/ApiService";
import { sellOptionsUrl, sellSubmetalOptionsUrl, sellFinenessOptionsUrl } from "../../../common/config/app.endpoints";

interface WizardStep2AIReadProps {
  aiData: any;
  setAiData: any;
  initialAiData?: any;
  isSubmitting?: boolean;
  validationErrors?: string[];
}

export const isOtherJewelleryMetal = (metal: string | null | undefined): boolean => {
  if (!metal) return false;
  const m = metal.trim().toLowerCase();
  const preciousMetals = ['gold', 'silver', 'platinum', 'palladium'];
  if (preciousMetals.some((p) => m.includes(p))) {
    return false;
  }
  return true;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("id_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const metalOptionsList = [
  { group: 'Precious Metals', value: 'Gold' },
  { group: 'Precious Metals', value: 'Silver' },
  { group: 'Precious Metals', value: 'Platinum' },
  { group: 'Precious Metals', value: 'Palladium' },
  { group: 'Other Jewellery Metals', value: 'Titanium' },
  { group: 'Other Jewellery Metals', value: 'Stainless Steel' },
  { group: 'Other Jewellery Metals', value: 'Tungsten' },
  { group: 'Other Jewellery Metals', value: 'Brass' },
  { group: 'Other Jewellery Metals', value: 'Copper' },
  { group: 'Other Jewellery Metals', value: 'Bronze' },
  { group: 'Other Jewellery Metals', value: 'Nickel / Nickel Alloy' },
];

const getOptionsArray = (optionsObj: any) => {
  if (!optionsObj) return [];
  return Object.entries(optionsObj)
    .filter(([key]) => key !== "")
    .map(([key, label]) => ({ key, label: label as string }));
};

const getAutocompleteValue = (optionsArr: any[], value: any, strict = false) => {
  if (value === null || value === undefined || value === "") return null;
  const strValue = typeof value === 'string' ? value : String(value);
  const lowerValue = strValue.toLowerCase().trim();

  let found = optionsArr.find(o => o.key.toLowerCase() === lowerValue || o.label.toLowerCase() === lowerValue);

  if (!found && !strict) {
    const normalize = (str: string) => str.replace(/z/g, 's').replace(/[^a-z0-9]/g, '');
    const normalizedValue = normalize(lowerValue);
    found = optionsArr.find(o => {
      return normalize(o.key.toLowerCase()) === normalizedValue || normalize(o.label.toLowerCase()) === normalizedValue;
    });
  }

  if (!found) {
    found = optionsArr.find(o => {
      const lowerLabel = o.label.toLowerCase();
      if (lowerValue.length <= 2) {
        // Prevent single/double letter options from matching long words haphazardly
        return lowerLabel === lowerValue || lowerLabel.startsWith(lowerValue + "-");
      }
      return lowerLabel.includes(lowerValue) || lowerValue.includes(lowerLabel);
    });
  }

  return found || null;
};

const getBadgeStyle = (type: 'photo' | 'hallmark' | 'certificate' | 'verified' | 'user' | 'combined') => {
  if (type === 'combined') {
    return {
      fontSize: '0.6rem',
      fontWeight: 700,
      padding: '0.15rem 0.5rem',
      borderRadius: '12px',
      background: 'transparent',
      color: '#388e3c',
      border: '1px solid #c8e6c9',
      letterSpacing: '0.5px',
      textTransform: 'uppercase' as const,
    };
  }
  const isPhoto = type === 'photo';
  const isUser = type === 'user';
  return {
    fontSize: '0.6rem',
    fontWeight: 700,
    padding: '0.15rem 0.5rem',
    borderRadius: '12px',
    background: 'transparent',
    color: isPhoto || isUser ? '#d92b3c' : '#999',
    border: `1px solid ${isPhoto || isUser ? '#ffb3c6' : '#EAEAEA'}`,
    letterSpacing: '0.5px',
    textTransform: 'uppercase' as const,
  };
};

const WizardStep2AIRead: React.FC<WizardStep2AIReadProps> = ({ aiData, setAiData, initialAiData, isSubmitting = false, validationErrors = [] }) => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [options, setOptions] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!options) return;
    
    let changed = false;
    const updates: any = {};

    const colorOpts = getOptionsArray(options.colorOptions);

    if (aiData?.color && typeof aiData.color === 'string' && colorOpts.length > 0) {
      const match = getAutocompleteValue(colorOpts, aiData.color, true);
      if (!match) {
        updates.color = "";
        changed = true;
      } else if (match.key !== aiData.color) {
        updates.color = match.key;
        changed = true;
      }
    }

    if (aiData?.sideStoneColor && colorOpts.length > 0) {
      const asArray = Array.isArray(aiData.sideStoneColor) ? aiData.sideStoneColor : [aiData.sideStoneColor];
      const cleanArray = asArray.map((c: any) => {
        const match = getAutocompleteValue(colorOpts, c, true);
        return match ? match.key : "";
      }).filter(Boolean);
      if (JSON.stringify(cleanArray) !== JSON.stringify(asArray)) {
        updates.sideStoneColor = cleanArray;
        changed = true;
      }
    }

    if (changed) {
      setAiData((prev: any) => ({ ...prev, ...updates }));
    }
  }, [options, aiData?.color, aiData?.sideStoneColor, setAiData]);

  const aiItemType = aiData && aiData.itemType;
  const aiCategory = aiData && aiData.category;
  const itemTypeStr = (aiItemType || aiCategory || '').trim().toLowerCase();
  const isDiamond = itemTypeStr === 'diamond' || itemTypeStr === 'diamonds' || itemTypeStr.includes('diamond');

  const aiCentreStone = aiData && aiData.centreStone;
  const aiCenterStone = aiData && aiData.centerStone;
  const hasCenterStone = Boolean(aiData?.centreStoneEnabled);

  const aiSideStone = aiData && aiData.sideStone;
  const aiSideStones = aiData && aiData.sideStones;
  const aiSide_stone = aiData && aiData.side_stone;
  const aiSide_stones = aiData && aiData.side_stones;
  const hasSideStone = Boolean(
    (aiSideStone && aiSideStone.toString().trim() !== "") ||
    (aiSideStones && aiSideStones.toString().trim() !== "") ||
    (aiSide_stone && aiSide_stone.toString().trim() !== "") ||
    (aiSide_stones && aiSide_stones.toString().trim() !== "") ||
    (aiData?.sideStoneShape && aiData.sideStoneShape.toString().trim() !== "") ||
    (aiData?.sideStoneCut && aiData.sideStoneCut.toString().trim() !== "") ||
    (aiData?.sideStoneColor && aiData.sideStoneColor.toString().trim() !== "") ||
    (aiData?.sideStoneClarity && aiData.sideStoneClarity.toString().trim() !== "") ||
    (aiData?.sideStoneCarat !== undefined && aiData?.sideStoneCarat !== null && aiData.sideStoneCarat.toString().trim() !== "" && Number(aiData.sideStoneCarat) !== 0) ||
    (aiData?.noOfSideStones !== undefined && aiData?.noOfSideStones !== null && aiData.noOfSideStones.toString().trim() !== "" && Number(aiData.noOfSideStones) !== 0)
  );

  const aiHasStone = aiData && aiData.hasStone;
  const aiHasStones = aiData && aiData.hasStones;
  const aiStone = aiData && aiData.stone;
  const aiStones = aiData && aiData.stones;
  const hasStone = isDiamond || hasCenterStone || hasSideStone || Boolean(aiHasStone) || Boolean(aiHasStones) || Boolean(aiStone && aiStone.toString().trim() !== "") || Boolean(aiStones && aiStones.toString().trim() !== "");

  const [centerStoneSectionEnabled, setCenterStoneSectionEnabled] = useState(hasCenterStone || isDiamond);
  const [sideStoneSectionEnabled, setSideStoneSectionEnabled] = useState(hasSideStone);

  const [sideStoneModalOpen, setSideStoneModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [sideStoneDraft, setSideStoneDraft] = useState({
    stoneType: '',
    shape: '',
    cut: '',
    carat: '',
    color: '',
    clarity: '',
    count: '',
  });
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const sideStonesList = aiData?.IndividualCaratBreakdown || aiData?.individualCaratBreakdown || aiData?.SideStone?.IndividualCaratBreakdown || aiData?.sideStone?.IndividualCaratBreakdown || aiData?.sideStone?.individualCaratBreakdown || [];

  useEffect(() => {
    if (sideStoneModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [sideStoneModalOpen]);

  const openModalWithSync = () => {
    let currentList = aiData?.IndividualCaratBreakdown || aiData?.individualCaratBreakdown || aiData?.SideStone?.IndividualCaratBreakdown || aiData?.sideStone?.IndividualCaratBreakdown || aiData?.sideStone?.individualCaratBreakdown || [];
    const hasMainGridData = aiData?.sideStone?.length > 0 || aiData?.sideStoneShape?.length > 0 || aiData?.sideStoneCut?.length > 0 || aiData?.sideStoneCarat || aiData?.sideStoneColor?.length > 0 || aiData?.sideStoneClarity?.length > 0 || aiData?.noOfSideStones;

    if (currentList.length === 0 && hasMainGridData) {
      const fallbackGroup = {
        type: Array.isArray(aiData?.sideStone) ? aiData.sideStone[0] : (aiData?.sideStone || null),
        shape: Array.isArray(aiData?.sideStoneShape) ? aiData.sideStoneShape[0] : (aiData?.sideStoneShape || null),
        cut: Array.isArray(aiData?.sideStoneCut) ? aiData.sideStoneCut[0] : (aiData?.sideStoneCut || null),
        carat: aiData?.sideStoneCarat ? Number(aiData.sideStoneCarat) : null,
        color: Array.isArray(aiData?.sideStoneColor) ? aiData.sideStoneColor[0] : (aiData?.sideStoneColor || null),
        clarity: Array.isArray(aiData?.sideStoneClarity) ? aiData.sideStoneClarity[0] : (aiData?.sideStoneClarity || null),
        count: aiData?.noOfSideStones ? Number(aiData.noOfSideStones) : null,
      };
      currentList = [fallbackGroup];
      setAiData((prev: any) => ({ ...prev, IndividualCaratBreakdown: currentList }));
    }

    if (currentList.length === 0) {
      setSideStoneDraft({ stoneType: '', shape: '', cut: '', carat: '', color: '', clarity: '', count: '' });
      setEditingIndex(null);
      setIsAddingNew(true);
    } else {
      setIsAddingNew(false);
    }
    setSideStoneModalOpen(true);
  };


  const saveSideStoneDraft = () => {
    if (
      !sideStoneDraft.stoneType ||
      !sideStoneDraft.shape ||
      !sideStoneDraft.cut ||
      !sideStoneDraft.carat ||
      !sideStoneDraft.color ||
      !sideStoneDraft.clarity ||
      !sideStoneDraft.count
    ) {
      setDraftError("Please fill out all details before saving.");
      return;
    }
    setDraftError(null);

    const updatedList = [...sideStonesList];
    const draftObj = {
      type: sideStoneDraft.stoneType || null,
      shape: sideStoneDraft.shape || null,
      cut: sideStoneDraft.cut || null,
      carat: sideStoneDraft.carat ? Number(sideStoneDraft.carat) : null,
      color: sideStoneDraft.color || null,
      clarity: sideStoneDraft.clarity || null,
      count: sideStoneDraft.count ? Number(sideStoneDraft.count) : null,
    };

    if (editingIndex !== null) {
      updatedList[editingIndex] = draftObj;
    } else {
      updatedList.push(draftObj);
    }

    const types = Array.from(new Set(updatedList.map(s => s.type).filter(Boolean)));
    const shapes = Array.from(new Set(updatedList.map(s => s.shape).filter(Boolean)));
    const cuts = Array.from(new Set(updatedList.map(s => s.cut).filter(Boolean)));
    const colors = Array.from(new Set(updatedList.map(s => s.color).filter(Boolean)));
    const clarities = Array.from(new Set(updatedList.map(s => s.clarity).filter(Boolean)));

    const totalCarat = updatedList.reduce((sum, s) => sum + (Number(s.carat) || 0), 0);
    const totalCount = updatedList.reduce((sum, s) => sum + (Number(s.count) || 0), 0);

    setAiData((prev: any) => ({
      ...prev,
      IndividualCaratBreakdown: updatedList,
      sideStone: types.length ? types : null,
      sideStoneShape: shapes.length ? shapes : null,
      sideStoneCut: cuts.length ? cuts : null,
      sideStoneColor: colors.length ? colors : null,
      sideStoneClarity: clarities.length ? clarities : null,
      sideStoneCarat: totalCarat > 0 ? Number(totalCarat.toFixed(3)) : null,
      noOfSideStones: totalCount > 0 ? totalCount : null,
    }));

    setIsAddingNew(false);
    setEditingIndex(null);
    setDraftError(null);
  };

  const removeSideStone = (index: number) => {
    const updatedList = [...sideStonesList];
    updatedList.splice(index, 1);

    const types = Array.from(new Set(updatedList.map(s => s.type).filter(Boolean)));
    const shapes = Array.from(new Set(updatedList.map(s => s.shape).filter(Boolean)));
    const cuts = Array.from(new Set(updatedList.map(s => s.cut).filter(Boolean)));
    const colors = Array.from(new Set(updatedList.map(s => s.color).filter(Boolean)));
    const clarities = Array.from(new Set(updatedList.map(s => s.clarity).filter(Boolean)));

    const totalCarat = updatedList.reduce((sum, s) => sum + (Number(s.carat) || 0), 0);
    const totalCount = updatedList.reduce((sum, s) => sum + (Number(s.count) || 0), 0);

    setAiData((prev: any) => ({
      ...prev,
      IndividualCaratBreakdown: updatedList,
      sideStone: types.length ? types : null,
      sideStoneShape: shapes.length ? shapes : null,
      sideStoneCut: cuts.length ? cuts : null,
      sideStoneColor: colors.length ? colors : null,
      sideStoneClarity: clarities.length ? clarities : null,
      sideStoneCarat: totalCarat > 0 ? Number(totalCarat.toFixed(3)) : null,
      noOfSideStones: totalCount > 0 ? totalCount : null,
    }));
  };

  const openEditModal = (index: number) => {
    const s = sideStonesList[index];
    setSideStoneDraft({
      stoneType: s.type || '',
      shape: s.shape || '',
      cut: s.cut || '',
      carat: s.carat?.toString() || '',
      color: s.color || '',
      clarity: s.clarity || '',
      count: s.count?.toString() || '',
    });
    setEditingIndex(index);
    setIsAddingNew(true);
  };

  const openAddModal = () => {
    setSideStoneDraft({ stoneType: '', shape: '', cut: '', carat: '', color: '', clarity: '', count: '' });
    setEditingIndex(null);
    setIsAddingNew(true);
  };

  // Sync section enabled states if aiData changes externally
  useEffect(() => {
    if (aiData?.centreStoneEnabled !== undefined) {
      setCenterStoneSectionEnabled(aiData.centreStoneEnabled || isDiamond);
    } else {
      setCenterStoneSectionEnabled(hasCenterStone || isDiamond);
    }
    
    if (aiData?.sideStoneEnabled !== undefined) {
      setSideStoneSectionEnabled(aiData.sideStoneEnabled);
    } else {
      setSideStoneSectionEnabled(hasSideStone);
    }
  }, [hasCenterStone, isDiamond, hasSideStone, aiData?.sideStoneEnabled, aiData?.centreStoneEnabled]);

  const customMenuProps = {
    PaperProps: {
      style: {
        maxHeight: 280,
      },
    },
    getContentAnchorEl: null,
    anchorOrigin: {
      vertical: "bottom" as const,
      horizontal: "left" as const,
    },
    transformOrigin: {
      vertical: "top" as const,
      horizontal: "left" as const,
    },
  };


  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await publicApi.get(sellOptionsUrl);
        setOptions(response.data);
      } catch (error) {
        console.error("Failed to fetch sell options:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchOptions();
  }, []);

  const [subMetalOptions, setSubMetalOptions] = useState<string[]>([]);
  const [purityOptions, setPurityOptions] = useState<string[]>([]);

  // API 1: Get Sub-Metals List
  useEffect(() => {
    const selectedMetal = aiData?.metal;
    if (!selectedMetal || selectedMetal.trim() === "") {
      setSubMetalOptions([]);
      return;
    }

    let isMounted = true;

    const fetchSubMetals = async () => {
      try {
        const response = await publicApi.get(
          `${sellSubmetalOptionsUrl}?metal=${encodeURIComponent(selectedMetal.trim())}`,
          { headers: getAuthHeaders() }
        );

        if (isMounted && response?.data) {
          const list = Array.isArray(response.data) ? response.data : [];
          setSubMetalOptions(list);
        }
      } catch (err) {
        console.error("Failed to fetch submetal-options:", err);
      }
    };

    fetchSubMetals();

    return () => {
      isMounted = false;
    };
  }, [aiData?.metal]);

  // API 2: Get Fineness List
  useEffect(() => {
    const selectedSubMetal = aiData?.subMetal;
    const selectedMetal = aiData?.metal;

    if (!selectedSubMetal || selectedSubMetal.trim() === "" || isOtherJewelleryMetal(selectedMetal)) {
      setPurityOptions([]);
      return;
    }

    let isMounted = true;

    const fetchFineness = async () => {
      try {
        const params = new URLSearchParams();
        params.append("subMetal", selectedSubMetal.trim());
        if (selectedMetal && selectedMetal.trim() !== "") {
          params.append("metal", selectedMetal.trim());
        }

        const response = await publicApi.get(
          `${sellFinenessOptionsUrl}?${params.toString()}`,
          { headers: getAuthHeaders() }
        );

        if (isMounted && response?.data) {
          const list = Array.isArray(response.data) ? response.data : [];
          setPurityOptions(list);
        }
      } catch (err) {
        console.error("Failed to fetch fineness-options:", err);
      }
    };

    fetchFineness();

    return () => {
      isMounted = false;
    };
  }, [aiData?.subMetal, aiData?.metal]);

  const optShapeOptions = options && options.shapeOptions;
  const optCutGradeOptions = options && options.cutGradeOptions;
  const optColorOptions = options && options.colorOptions;
  const optClarityOptions = options && options.clarityOptions;
  const optStoneOptions = options && options.stoneOptions;

  const shapeOptions = getOptionsArray(optShapeOptions);
  const baseCutOptions = getOptionsArray(optCutGradeOptions);
  const cutOptions = baseCutOptions.some((o: any) => o.key === "****")
    ? baseCutOptions
    : [{ key: "****", label: "****" }, ...baseCutOptions];
  const colorOptions = getOptionsArray(optColorOptions);
  const clarityOptions = getOptionsArray(optClarityOptions);
  const stoneOptions = getOptionsArray(optStoneOptions) || [];


  const colorFilterOptions = createFilterOptions({ matchFrom: 'start' });

  const getAutocompleteMultipleValue = (optionsArr: any[], value: any, strict = false) => {
    if (!value) return [];
    const values = Array.isArray(value) ? value : String(value).split(',').map(s => s.trim()).filter(Boolean);
    return values.map(v => {
      const found = getAutocompleteValue(optionsArr, v, strict);
      return found || (strict ? null : (typeof v === 'object' ? v : { key: String(v), label: String(v) }));
    }).filter(Boolean);
  };

  const renderHorizontalScrollTags = (value: any[], getTagProps: any) => {
    if (!value || value.length === 0) return null;
    return (
      <div style={{
        display: 'flex',
        flexWrap: 'nowrap',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        width: '100%',
        alignItems: 'center'
      }}>
        {value.map((option, index) => {
          const { onDelete, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              size="small"
              label={option.label || option.key || String(option)}
              {...tagProps}
              style={{
                flexShrink: 0,
                marginRight: '4px',
                height: '24px',
                backgroundColor: 'rgba(217, 43, 60, 0.08)',
                color: '#333333',
                border: '1px solid rgba(217, 43, 60, 0.2)',
                fontWeight: 500,
                fontSize: '0.75rem'
              }}
            />
          );
        })}
      </div>
    );
  };

  const renderCheckboxOption = (option: any, { selected }: any) => (
    <React.Fragment>
      <Checkbox
        checked={selected}
        size="small"
        style={{ padding: 0, marginRight: 8, color: '#d92b3c' }}
      />
      {option.label || option.key || String(option)}
    </React.Fragment>
  );

  const isOther = isOtherJewelleryMetal(aiData?.metal);

  const isMandatory = {
    itemType: true,
    metal: !isDiamond,
    metalPurity: !isDiamond && !isOther,
    centreStone: !isDiamond && centerStoneSectionEnabled,
    carat: isDiamond || centerStoneSectionEnabled,
    shape: isDiamond || centerStoneSectionEnabled,
    cut: isDiamond || centerStoneSectionEnabled,
    color: isDiamond || centerStoneSectionEnabled,
    clarity: isDiamond || centerStoneSectionEnabled,
  };

  // Hooks moved to top

  const handleCenterStoneToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setCenterStoneSectionEnabled(checked);
    if (!checked) {
      setAiData((prev: any) => ({
        ...prev,
        centreStoneEnabled: false,
      }));
    } else {
      setAiData((prev: any) => ({
        ...prev,
        centreStoneEnabled: true,
      }));
    }
  };

  const handleSideStoneToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSideStoneSectionEnabled(checked);
    if (!checked) {
      setAiData((prev: any) => ({
        ...prev,
        sideStoneEnabled: false,
      }));
    } else {
      setAiData((prev: any) => ({
        ...prev,
        sideStoneEnabled: true,
      }));
    }
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Typography variant="h5" style={{ fontWeight: 'bold', marginBottom: '0.5rem', fontFamily: '"Playfair Display", serif', fontSize: '1.75rem', color: '#1A1A1A' }}>Confirm what we read</Typography>
        <Typography style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.95rem' }}>Correct anything that's off — accuracy here sharpens your price.</Typography>

        <div style={{ background: '#FFF0F2', border: '1px solid #FCEBEA', padding: '1rem', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#1A1A1A', fontSize: '0.85rem' }}>
          <span style={{ fontSize: '1.2rem', lineHeight: 1, color: '#d92b3c' }}>✦</span>
          <span>We read these from your photos and certificate. Anything marked <span style={{ fontWeight: 'bold', color: '#d92b3c' }}>from photo</span> is our best read, not a fact — check it and it becomes yours. Certificate values are taken as given.</span>
        </div>
      </Grid>

      {/* General Details Section */}
      <Grid item xs={12}>
        <div style={{ border: '1px solid #EAEAEA', borderRadius: '12px', padding: '1.5rem', background: '#FAFAFA' }}>
          <Grid container spacing={4}>
            {/* ROW 1 */}
            <Grid item xs={12} sm={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>ITEM TYPE {isMandatory.itemType && <span style={{ color: '#d92b3c' }}>*</span>}</span>
                <span style={getBadgeStyle('photo')}>FROM PHOTO</span>
              </div>
              <CustomTextField variant="outlined" fullWidth size="small" disabled={isSubmitting} InputProps={{ readOnly: true }} value={aiData?.itemType ?? aiData?.category ?? ""} onChange={(e) => { const val = e.target.value; setAiData((prev: any) => ({ ...prev, itemType: val })) }} style={{ background: '#f5f5f5' }} />
              {isMandatory.itemType && validationErrors.includes('Item Type') && (!aiData?.itemType || aiData.itemType.trim() === "") && <span style={{ color: '#d92b3c', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block', fontWeight: 500 }}>* Item Type is mandatory.</span>}
            </Grid>

            {!isDiamond && (
              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>METAL {isMandatory.metal && <span style={{ color: '#d92b3c' }}>*</span>}</span>
                  <span style={getBadgeStyle('hallmark')}>FROM HALLMARK</span>
                </div>
                <Autocomplete
                  size="small"
                  freeSolo
                  forcePopupIcon={true}
                  disabled={isSubmitting}
                  options={metalOptionsList}
                  groupBy={(option: any) => option.group}
                  getOptionLabel={(option: any) => typeof option === 'string' ? option : option.value}
                  value={aiData?.metal || ""}
                  onChange={(event, newValue: any) => {
                    const val = typeof newValue === 'string' ? newValue : newValue?.value || "";
                    const newMetal = val.trim() === "" ? null : val;
                    const originalMetal = initialAiData?.metal ?? null;
                    const metalChanged = (newMetal || "").trim().toLowerCase() !== (originalMetal || "").trim().toLowerCase();
                    const isNewMetalOther = isOtherJewelleryMetal(newMetal);
                    setAiData((prev: any) => ({
                      ...prev,
                      metal: newMetal,
                      subMetal: metalChanged ? null : prev.subMetal,
                      metalPurity: (metalChanged || isNewMetalOther) ? null : prev.metalPurity,
                    }));
                  }}
                  onInputChange={(event, newInputValue) => {
                    const trimmed = newInputValue.trim();
                    const newMetal = trimmed === "" ? null : newInputValue;
                    const isNewMetalOther = isOtherJewelleryMetal(newMetal);
                    setAiData((prev: any) => ({
                      ...prev,
                      metal: newMetal,
                      metalPurity: isNewMetalOther ? null : prev.metalPurity,
                    }));
                  }}
                  renderInput={(params) => <CustomTextField {...params} variant="outlined" style={{ background: '#fff' }} />}
                  ListboxProps={{ style: { maxHeight: 280, fontSize: '0.85rem' } }}
                />
                {isMandatory.metal && validationErrors.includes('Metal') && (!aiData?.metal || aiData.metal.trim() === "") && <span style={{ color: '#d92b3c', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block', fontWeight: 500 }}>* Metal is mandatory.</span>}
              </Grid>
            )}

            {/* ROW 2 */}
            {!isDiamond && (
              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>SUB METAL</span>
                  <span style={getBadgeStyle('hallmark')}>FROM HALLMARK</span>
                </div>
                <Autocomplete
                  size="small"
                  freeSolo
                  forcePopupIcon={true}
                  disabled={isSubmitting}
                  options={subMetalOptions}
                  getOptionLabel={(option: any) => typeof option === 'string' ? option : (option?.label || option?.value || String(option))}
                  value={aiData?.subMetal || ""}
                  onChange={(event, newValue: any) => {
                    const val = typeof newValue === 'string' ? newValue : (newValue?.value || newValue?.label || newValue || null);
                    setAiData((prev: any) => ({ ...prev, subMetal: val || null, metalPurity: null }));
                  }}
                  onInputChange={(event, newInputValue) => {
                    setAiData((prev: any) => ({ ...prev, subMetal: newInputValue.trim() === "" ? null : newInputValue }));
                  }}
                  renderInput={(params) => <CustomTextField {...params} variant="outlined" style={{ background: '#fff' }} />}
                  ListboxProps={{ style: { maxHeight: 280, fontSize: '0.85rem' } }}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>CERTIFICATE</span>
                <span style={getBadgeStyle('verified')}>VERIFIED</span>
              </div>
              <CustomTextField variant="outlined" fullWidth size="small" disabled={isSubmitting} value={aiData?.certificateAndReportNumber || ""} onChange={(e) => { const val = e.target.value; setAiData((prev: any) => ({ ...prev, certificateAndReportNumber: val })) }} style={{ background: '#fff' }} />
            </Grid>

            {/* ROW 3 */}
            {!isDiamond && !isOther && (
              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>METAL FINENESS / PURITY {isMandatory.metalPurity && <span style={{ color: '#d92b3c' }}>*</span>}</span>
                  <span style={getBadgeStyle('hallmark')}>FROM HALLMARK</span>
                </div>
                <Autocomplete
                  size="small"
                  freeSolo
                  forcePopupIcon={true}
                  disabled={isSubmitting}
                  options={purityOptions}
                  getOptionLabel={(option: any) => typeof option === 'string' ? option : (option?.label || option?.value || String(option))}
                  value={aiData?.metalPurity || ""}
                  onChange={(event, newValue: any) => {
                    const val = typeof newValue === 'string' ? newValue : (newValue?.value || newValue?.label || newValue || null);
                    setAiData((prev: any) => ({ ...prev, metalPurity: val || null }));
                  }}
                  onInputChange={(event, newInputValue) => {
                    setAiData((prev: any) => ({ ...prev, metalPurity: newInputValue.trim() === "" ? null : newInputValue }));
                  }}
                  renderInput={(params) => <CustomTextField {...params} variant="outlined" style={{ background: '#fff' }} />}
                  ListboxProps={{ style: { maxHeight: 280, fontSize: '0.85rem' } }}
                />
                {isMandatory.metalPurity && validationErrors.includes('Metal Purity') && (!aiData?.metalPurity || aiData.metalPurity.trim() === "") && (
                  <span style={{ color: '#d92b3c', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block', fontWeight: 500 }}>* Metal Purity is mandatory.</span>
                )}
              </Grid>
            )}

          </Grid>
        </div>
      </Grid>

      {/* Centre Stone Section */}
      <Grid item xs={12}>
        <div style={{ border: '1px solid #EAEAEA', borderRadius: '12px', padding: '1.5rem', background: '#FAFAFA' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem', borderBottom: centerStoneSectionEnabled ? '1px solid #EAEAEA' : 'none', paddingBottom: centerStoneSectionEnabled ? '1rem' : '0' }}>
            <Checkbox
              checked={centerStoneSectionEnabled}
              onChange={handleCenterStoneToggle}
              disabled={isSubmitting}
              style={{ color: '#d92b3c', padding: '4px', marginRight: '8px' }}
            />
            <span style={{ fontSize: '0.85rem', fontWeight: 700, letterSpacing: '1.5px', color: '#1A1A1A', textTransform: 'uppercase' }}>CENTRE STONE</span>
          </div>

          {centerStoneSectionEnabled && (
            <Grid container spacing={4}>
              {!isDiamond && (
                <Grid item xs={12} sm={6}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>STONE TYPE {isMandatory.centreStone && <span style={{ color: '#d92b3c' }}>*</span>}</span>
                    <span style={getBadgeStyle('photo')}>FROM PHOTO</span>
                  </div>
                  <CustomTextField
                    variant="outlined"
                    fullWidth
                    size="small"
                    disabled={isSubmitting}
                    value={aiData?.centreStone || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      setAiData((prev: any) => {
                        const newCentreStone = val.trim() === "" ? null : val;
                        if (!newCentreStone) {
                          return {
                            ...prev,
                            centreStone: null,
                            shape: null,
                            cut: null,
                            carat: null,
                            color: null,
                            clarity: null,
                          };
                        }
                        return { ...prev, centreStone: val };
                      });
                    }}
                    style={{ background: '#fff' }}
                  />
                  {isMandatory.centreStone && validationErrors.includes('Centre Stone') && (!aiData?.centreStone || aiData.centreStone.trim() === "") && <span style={{ color: '#d92b3c', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block', fontWeight: 500 }}>* Centre Stone is mandatory.</span>}
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>SHAPE {isMandatory.shape && <span style={{ color: '#d92b3c' }}>*</span>}</span>
                  <span style={getBadgeStyle('photo')}>FROM PHOTO</span>
                </div>
                <Autocomplete
                  size="small"
                  disabled={isLoading || isSubmitting}
                  options={shapeOptions}
                  getOptionLabel={(option: any) => option.label}
                  value={getAutocompleteValue(shapeOptions, aiData?.shape)}
                  onChange={(event, newValue: any) => {
                    setAiData((prev: any) => ({ ...prev, shape: newValue ? newValue.key : "" }));
                  }}
                  renderInput={(params) => <CustomTextField {...params} variant="outlined" placeholder="--- Please Select ---" style={{ background: '#fff' }} />}
                  ListboxProps={{ style: { maxHeight: 280, fontSize: '0.85rem' } }}
                />
                {isMandatory.shape && validationErrors.includes('Shape') && (!aiData?.shape || aiData.shape.trim() === "") && <span style={{ color: '#d92b3c', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block', fontWeight: 500 }}>* Shape is mandatory.</span>}
              </Grid>

              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>CUT {isMandatory.cut && <span style={{ color: '#d92b3c' }}>*</span>}</span>
                  <span style={getBadgeStyle('photo')}>FROM PHOTO</span>
                </div>
                <Autocomplete
                  size="small"
                  disabled={isLoading || isSubmitting}
                  options={cutOptions}
                  getOptionLabel={(option: any) => option.label}
                  value={getAutocompleteValue(cutOptions, aiData?.cut)}
                  onChange={(event, newValue: any) => {
                    setAiData((prev: any) => ({ ...prev, cut: newValue ? newValue.key : "" }));
                  }}
                  renderInput={(params) => <CustomTextField {...params} variant="outlined" placeholder="--- Please Select ---" style={{ background: '#fff' }} />}
                  ListboxProps={{ style: { maxHeight: 280, fontSize: '0.85rem' } }}
                />
                {isMandatory.cut && validationErrors.includes('Cut') && (!aiData?.cut || aiData.cut.trim() === "") && <span style={{ color: '#d92b3c', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block', fontWeight: 500 }}>* Cut is mandatory.</span>}
              </Grid>

              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>CARAT (CENTRE) {isMandatory.carat && <span style={{ color: '#d92b3c' }}>*</span>}</span>
                  <span style={getBadgeStyle('certificate')}>FROM CERTIFICATE</span>
                </div>
                <CustomTextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  disabled={isSubmitting}
                  value={aiData?.carat ?? ""}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (val === '.') val = '0.';
                    else if (val.startsWith('.')) val = '0' + val;
                    setAiData((prev: any) => ({ ...prev, carat: val }))
                  }}
                  style={{ background: '#fff' }}
                />
                {isMandatory.carat && validationErrors.includes('Carat (Centre)') && (!aiData?.carat || aiData.carat.toString().trim() === "") && <span style={{ color: '#d92b3c', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block', fontWeight: 500 }}>* Carat is mandatory.</span>}
              </Grid>

              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>COLOUR {isMandatory.color && <span style={{ color: '#d92b3c' }}>*</span>}</span>
                  <span style={getBadgeStyle('photo')}>FROM PHOTO</span>
                </div>
                <Autocomplete
                  size="small"
                  disabled={isLoading || isSubmitting}
                  options={colorOptions}
                  filterOptions={colorFilterOptions}
                  getOptionLabel={(option: any) => option.label}
                  value={getAutocompleteValue(colorOptions, aiData?.color, true)}
                  onChange={(event, newValue: any) => {
                    setAiData((prev: any) => ({ ...prev, color: newValue ? newValue.key : "" }));
                  }}
                  renderInput={(params) => <CustomTextField {...params} variant="outlined" placeholder="--- Please Select ---" style={{ background: '#fff' }} />}
                  ListboxProps={{ style: { maxHeight: 280, fontSize: '0.85rem' } }}
                />
                {isMandatory.color && validationErrors.includes('Colour') && (!aiData?.color || aiData.color.trim() === "") && <span style={{ color: '#d92b3c', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block', fontWeight: 500 }}>* Colour is mandatory.</span>}
              </Grid>

              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>CLARITY {isMandatory.clarity && <span style={{ color: '#d92b3c' }}>*</span>}</span>
                  <span style={getBadgeStyle('photo')}>FROM PHOTO</span>
                </div>
                <Autocomplete
                  size="small"
                  disabled={isLoading || isSubmitting}
                  options={clarityOptions}
                  getOptionLabel={(option: any) => option.label}
                  value={getAutocompleteValue(clarityOptions, aiData?.clarity)}
                  onChange={(event, newValue: any) => {
                    setAiData((prev: any) => ({ ...prev, clarity: newValue ? newValue.key : "" }));
                  }}
                  renderInput={(params) => <CustomTextField {...params} variant="outlined" placeholder="--- Please Select ---" style={{ background: '#fff' }} />}
                  ListboxProps={{ style: { maxHeight: 280, fontSize: '0.85rem' } }}
                />
                {isMandatory.clarity && validationErrors.includes('Clarity') && (!aiData?.clarity || aiData.clarity.trim() === "") && <span style={{ color: '#d92b3c', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block', fontWeight: 500 }}>* Clarity is mandatory.</span>}
              </Grid>
            </Grid>
          )}
        </div>
      </Grid>

      {/* Side Stone Section */}
      <Grid item xs={12}>
        <div style={{ border: '1px solid #EAEAEA', borderRadius: '12px', padding: '1.5rem', background: '#FAFAFA' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', borderBottom: sideStoneSectionEnabled ? '1px solid #EAEAEA' : 'none', paddingBottom: sideStoneSectionEnabled ? '1rem' : '0' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={sideStoneSectionEnabled}
                onChange={handleSideStoneToggle}
                disabled={isSubmitting}
                style={{ color: '#d92b3c', padding: '4px', marginRight: '8px' }}
              />
              <span style={{ fontSize: '0.85rem', fontWeight: 700, letterSpacing: '1.5px', color: '#1A1A1A', textTransform: 'uppercase' }}>SIDE STONE</span>
            </div>
            {sideStoneSectionEnabled && (
              <button 
                type="button" 
                onClick={openModalWithSync} 
                style={
                  sideStonesList.length >= 2
                    ? { border: 'none', background: '#fce8eb', color: '#d92b3c', padding: '0.55rem 1.25rem', borderRadius: '24px', fontWeight: 700, letterSpacing: '1px', fontSize: '0.7rem', cursor: 'pointer', transition: 'all 0.2s' }
                    : { border: '1px solid #d92b3c', background: '#fff', color: '#d92b3c', padding: '0.45rem 1rem', borderRadius: '24px', fontWeight: 700, letterSpacing: '1px', fontSize: '0.7rem', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(217,43,60,0.1)' }
                }
              >
                {sideStonesList.length >= 2 ? `Manage side stones (${sideStonesList.length})` : '+ Add side stones'}
              </button>
            )}
          </div>

          {sideStoneSectionEnabled && (
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>STONE TYPE {sideStoneSectionEnabled && <span style={{ color: '#d92b3c' }}>*</span>}</span>
                  {sideStonesList.length >= 2 ? <span style={getBadgeStyle('combined')}>COMBINED</span> : <span style={getBadgeStyle('photo')}>FROM PHOTO</span>}
                </div>
                <CustomTextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  disabled={isSubmitting}
                  placeholder={aiData?.sideStone?.length ? "" : (stoneOptions.length ? "--- Please Select ---" : "")}
                  inputProps={{ style: { display: 'none' } }}
                  InputProps={{
                    readOnly: true,
                    startAdornment: renderHorizontalScrollTags(
                      getAutocompleteMultipleValue(stoneOptions, aiData?.sideStone) || [],
                      () => ({})
                    ),
                    style: {
                      padding: '2px 8px 2px 8px',
                      minHeight: '40px',
                      flexWrap: 'nowrap',
                      alignItems: 'center'
                    }
                  }}
                  style={{ background: '#f5f5f5' }}
                />
                {sideStoneSectionEnabled && validationErrors.includes('Side Stone Type') && (!aiData?.sideStone || aiData.sideStone.length === 0) && <span style={{ color: '#d92b3c', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block', fontWeight: 500 }}>* Side Stone Type is mandatory.</span>}
              </Grid>

              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>SHAPE {sideStoneSectionEnabled && <span style={{ color: '#d92b3c' }}>*</span>}</span>
                  {sideStonesList.length >= 2 ? <span style={getBadgeStyle('combined')}>COMBINED</span> : <span style={getBadgeStyle('photo')}>FROM PHOTO</span>}
                </div>
                <CustomTextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  disabled={isSubmitting}
                  placeholder={aiData?.sideStoneShape?.length ? "" : (shapeOptions.length ? "--- Please Select ---" : "")}
                  inputProps={{ style: { display: 'none' } }}
                  InputProps={{
                    readOnly: true,
                    startAdornment: renderHorizontalScrollTags(
                      getAutocompleteMultipleValue(shapeOptions, aiData?.sideStoneShape) || [],
                      () => ({})
                    ),
                    style: {
                      padding: '2px 8px 2px 8px',
                      minHeight: '40px',
                      flexWrap: 'nowrap',
                      alignItems: 'center'
                    }
                  }}
                  style={{ background: '#f5f5f5' }}
                />
                {sideStoneSectionEnabled && validationErrors.includes('Side Shape') && (!aiData?.sideStoneShape || aiData.sideStoneShape.length === 0) && <span style={{ color: '#d92b3c', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block', fontWeight: 500 }}>* Side Shape is mandatory.</span>}
              </Grid>

              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>CUT {sideStoneSectionEnabled && <span style={{ color: '#d92b3c' }}>*</span>}</span>
                  {sideStonesList.length >= 2 ? <span style={getBadgeStyle('combined')}>COMBINED</span> : <span style={getBadgeStyle('photo')}>FROM PHOTO</span>}
                </div>
                <CustomTextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  disabled={isSubmitting}
                  placeholder={aiData?.sideStoneCut?.length ? "" : (cutOptions.length ? "--- Please Select ---" : "")}
                  inputProps={{ style: { display: 'none' } }}
                  InputProps={{
                    readOnly: true,
                    startAdornment: renderHorizontalScrollTags(
                      getAutocompleteMultipleValue(cutOptions, aiData?.sideStoneCut) || [],
                      () => ({})
                    ),
                    style: {
                      padding: '2px 8px 2px 8px',
                      minHeight: '40px',
                      flexWrap: 'nowrap',
                      alignItems: 'center'
                    }
                  }}
                  style={{ background: '#f5f5f5' }}
                />
                {sideStoneSectionEnabled && validationErrors.includes('Side Cut') && (!aiData?.sideStoneCut || aiData.sideStoneCut.length === 0) && <span style={{ color: '#d92b3c', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block', fontWeight: 500 }}>* Side Cut is mandatory.</span>}
              </Grid>

              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>{sideStonesList.length >= 2 ? 'CARAT (SIDE, TOTAL)' : 'CARAT (SIDE)'} {sideStoneSectionEnabled && <span style={{ color: '#d92b3c' }}>*</span>}</span>
                  {sideStonesList.length >= 2 ? <span style={getBadgeStyle('combined')}>COMBINED</span> : <span style={getBadgeStyle('certificate')}>FROM CERTIFICATE</span>}
                </div>
                <CustomTextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  disabled={isSubmitting}
                  InputProps={{ readOnly: true }}
                  value={Array.isArray(aiData?.sideStoneCarat) ? aiData.sideStoneCarat.join(', ') : (aiData?.sideStoneCarat ?? "")}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (val === '.') val = '0.';
                    else if (val.startsWith('.')) val = '0' + val;
                    setAiData((prev: any) => ({ ...prev, sideStoneCarat: val }))
                  }}
                  style={{ background: '#f5f5f5' }}
                />
                {sideStoneSectionEnabled && validationErrors.includes('Side Carat') && (!aiData?.sideStoneCarat || aiData.sideStoneCarat.length === 0) && <span style={{ color: '#d92b3c', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block', fontWeight: 500 }}>* Side Carat is mandatory.</span>}
              </Grid>

              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>COLOUR {sideStoneSectionEnabled && <span style={{ color: '#d92b3c' }}>*</span>}</span>
                  {sideStonesList.length >= 2 ? <span style={getBadgeStyle('combined')}>COMBINED</span> : <span style={getBadgeStyle('photo')}>FROM PHOTO</span>}
                </div>
                <CustomTextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  disabled={isSubmitting}
                  placeholder={aiData?.sideStoneColor?.length ? "" : (colorOptions.length ? "--- Please Select ---" : "")}
                  inputProps={{ style: { display: 'none' } }}
                  InputProps={{
                    readOnly: true,
                    startAdornment: renderHorizontalScrollTags(
                      getAutocompleteMultipleValue(colorOptions, aiData?.sideStoneColor, true) || [],
                      () => ({})
                    ),
                    style: {
                      padding: '2px 8px 2px 8px',
                      minHeight: '40px',
                      flexWrap: 'nowrap',
                      alignItems: 'center'
                    }
                  }}
                  style={{ background: '#f5f5f5' }}
                />
                {sideStoneSectionEnabled && validationErrors.includes('Side Colour') && (!aiData?.sideStoneColor || aiData.sideStoneColor.length === 0) && <span style={{ color: '#d92b3c', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block', fontWeight: 500 }}>* Side Colour is mandatory.</span>}
              </Grid>

              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>CLARITY {sideStoneSectionEnabled && <span style={{ color: '#d92b3c' }}>*</span>}</span>
                  {sideStonesList.length >= 2 ? <span style={getBadgeStyle('combined')}>COMBINED</span> : <span style={getBadgeStyle('photo')}>FROM PHOTO</span>}
                </div>
                <CustomTextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  disabled={isSubmitting}
                  placeholder={aiData?.sideStoneClarity?.length ? "" : (clarityOptions.length ? "--- Please Select ---" : "")}
                  inputProps={{ style: { display: 'none' } }}
                  InputProps={{
                    readOnly: true,
                    startAdornment: renderHorizontalScrollTags(
                      getAutocompleteMultipleValue(clarityOptions, aiData?.sideStoneClarity) || [],
                      () => ({})
                    ),
                    style: {
                      padding: '2px 8px 2px 8px',
                      minHeight: '40px',
                      flexWrap: 'nowrap',
                      alignItems: 'center'
                    }
                  }}
                  style={{ background: '#f5f5f5' }}
                />
                {sideStoneSectionEnabled && validationErrors.includes('Side Clarity') && (!aiData?.sideStoneClarity || aiData.sideStoneClarity.length === 0) && <span style={{ color: '#d92b3c', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block', fontWeight: 500 }}>* Side Clarity is mandatory.</span>}
              </Grid>

              <Grid item xs={12} sm={6}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666', textTransform: 'uppercase' }}>{sideStonesList.length >= 2 ? 'NO. OF STONES (TOTAL)' : 'NO. OF STONES'} {sideStoneSectionEnabled && <span style={{ color: '#d92b3c' }}>*</span>}</span>
                  {sideStonesList.length >= 2 ? <span style={getBadgeStyle('combined')}>COMBINED</span> : <span style={getBadgeStyle('photo')}>FROM PHOTO</span>}
                </div>
                <CustomTextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  disabled={isSubmitting}
                  InputProps={{ readOnly: true }}
                  value={Array.isArray(aiData?.noOfSideStones) ? aiData.noOfSideStones.join(', ') : (aiData?.noOfSideStones ?? "")}
                  onChange={(e) => {
                    const value = e.target.value;
                    setAiData((prev: any) => ({ ...prev, noOfSideStones: value }));
                  }}
                  style={{ background: '#f5f5f5' }}
                  type="text"
                />
                {sideStoneSectionEnabled && validationErrors.includes('Side Count') && (!aiData?.noOfSideStones || aiData.noOfSideStones.length === 0) && <span style={{ color: '#d92b3c', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block', fontWeight: 500 }}>* Side Stone Count is mandatory.</span>}
              </Grid>
              {sideStonesList.length >= 2 && (
                <Grid item xs={12}>
                  <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.5rem' }}>
                    <span style={{ color: '#d92b3c', fontWeight: 700 }}>{sideStonesList.length} side-stone groups</span> combined — use "Manage side stones" to edit or remove one.
                  </div>
                </Grid>
              )}
            </Grid>
          )}

          {sideStoneModalOpen && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1250 }} onClick={() => setSideStoneModalOpen(false)}>
              <div style={{ width: 'min(560px, calc(100vw - 2rem))', background: '#fff', borderRadius: '12px', boxShadow: '0 15px 35px rgba(0,0,0,0.18)', padding: '1.5rem 2rem', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#d92b3c', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', border: '1px solid #fce8eb', background: '#fff', borderRadius: '24px', padding: '0.35rem 0.85rem' }}>
                    <span>CONFIRM</span>
                    <span style={{ fontSize: '1rem', lineHeight: 0.5 }}>•</span>
                    <span>SIDE STONES</span>
                  </div>
                  <button type="button" onClick={() => setSideStoneModalOpen(false)} aria-label="Close" style={{ width: '24px', height: '24px', borderRadius: '50%', border: '1px solid #EAEAEA', background: '#fff', color: '#666', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.8rem', padding: 0 }}>
                    ✕
                  </button>
                </div>

                <div style={{ color: '#1A1A1A', marginBottom: '1.5rem' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>Side stones</div>
                  <div style={{ fontSize: '0.85rem', color: '#666', lineHeight: 1.5 }}>Add each group of side stones on the piece — e.g. six emeralds and ten round diamonds.<br />Everything here is combined into the side-stone section.</div>
                </div>

                {sideStonesList.length > 0 && (
                  <div style={{ maxHeight: '320px', overflowY: 'auto', paddingRight: '0.25rem', marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {sideStonesList.map((stone: any, index: number) => (
                      <div key={index} style={{ border: '1px solid #f0f0f0', borderRadius: '8px', padding: '1rem', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                          <div style={{ width: '1.75rem', height: '1.75rem', borderRadius: '50%', background: '#fce8eb', color: '#d92b3c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.75rem' }}>
                            {index + 1}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#333', lineHeight: 1.5 }}>
                            <div style={{ fontWeight: 600, marginBottom: '0.1rem', fontSize: '0.85rem' }}>{stone.type || 'Select stone'}</div>
                            <div style={{ color: '#666', fontSize: '0.75rem' }}>
                              {[
                                stone.cut,
                                stone.carat ? `${stone.carat} ct` : null,
                                stone.color,
                                stone.clarity,
                                stone.count ? `${stone.count} stones` : null
                              ].filter(Boolean).join(' • ')}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                          <button type="button" onClick={() => openEditModal(index)} style={{ border: 'none', background: 'transparent', color: '#999', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', cursor: 'pointer', textTransform: 'uppercase', padding: 0 }}>EDIT</button>
                          <button type="button" onClick={() => sideStonesList.length > 1 && removeSideStone(index)} disabled={sideStonesList.length <= 1} style={{ border: 'none', background: 'transparent', color: sideStonesList.length <= 1 ? '#ddd' : '#999', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', cursor: sideStonesList.length <= 1 ? 'not-allowed' : 'pointer', textTransform: 'uppercase', padding: 0 }}>REMOVE</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {isAddingNew && (
                  <div style={{ border: '1px solid #fce8eb', borderRadius: '12px', padding: '1.5rem', marginBottom: '1rem', background: '#fff' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1.5px', color: '#333', textTransform: 'uppercase', marginBottom: '1.5rem' }}>
                      {editingIndex !== null ? `EDIT SIDE STONE ${editingIndex + 1}` : `NEW SIDE STONE ${sideStonesList.length + 1}`}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem 1rem' }}>
                      <div>
                        <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', color: '#999', textTransform: 'uppercase', marginBottom: '0.4rem' }}>STONE TYPE <span style={{ color: '#d92b3c' }}>*</span></div>
                        <CustomTextField aria-label="STONE TYPE" variant="outlined" fullWidth size="small" value={sideStoneDraft.stoneType} onChange={(e) => { const val = e.target.value; setSideStoneDraft((prev) => ({ ...prev, stoneType: val })); }} style={{ background: '#fff' }} placeholder="e.g. Diamond, Ruby, Sapphire" />
                      </div>
                      <div>
                        <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', color: '#999', textTransform: 'uppercase', marginBottom: '0.4rem' }}>SHAPE <span style={{ color: '#d92b3c' }}>*</span></div>
                        <Autocomplete
                          size="small"
                          options={shapeOptions}
                          getOptionLabel={(option: any) => option.label}
                          value={getAutocompleteValue(shapeOptions, sideStoneDraft.shape)}
                          onChange={(event, newValue: any) => {
                            setSideStoneDraft((prev) => ({ ...prev, shape: newValue ? newValue.key : "" }));
                          }}
                          renderInput={(params) => <CustomTextField {...params} variant="outlined" placeholder="Select" style={{ background: '#fff' }} />}
                          ListboxProps={{ style: { maxHeight: 280, fontSize: '0.85rem' } }}
                        />
                      </div>

                      <div>
                        <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', color: '#999', textTransform: 'uppercase', marginBottom: '0.4rem' }}>CUT <span style={{ color: '#d92b3c' }}>*</span></div>
                        <Autocomplete
                          size="small"
                          options={cutOptions}
                          getOptionLabel={(option: any) => option.label}
                          value={getAutocompleteValue(cutOptions, sideStoneDraft.cut)}
                          onChange={(event, newValue: any) => {
                            setSideStoneDraft((prev) => ({ ...prev, cut: newValue ? newValue.key : "" }));
                          }}
                          renderInput={(params) => <CustomTextField {...params} variant="outlined" placeholder="Select" style={{ background: '#fff' }} />}
                          ListboxProps={{ style: { maxHeight: 280, fontSize: '0.85rem' } }}
                        />
                      </div>

                      <div>
                        <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', color: '#999', textTransform: 'uppercase', marginBottom: '0.4rem' }}>CARAT (SIDE) <span style={{ color: '#d92b3c' }}>*</span></div>
                        <CustomTextField aria-label="CARAT (SIDE)" variant="outlined" fullWidth size="small" value={sideStoneDraft.carat} onChange={(e) => { const val = e.target.value; setSideStoneDraft((prev) => ({ ...prev, carat: val })); }} style={{ background: '#fff' }} placeholder="e.g. 0.30 ct" />
                      </div>

                      <div>
                        <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', color: '#999', textTransform: 'uppercase', marginBottom: '0.4rem' }}>COLOUR <span style={{ color: '#d92b3c' }}>*</span></div>
                        <Autocomplete
                          size="small"
                          options={colorOptions}
                          filterOptions={colorFilterOptions}
                          getOptionLabel={(option: any) => option.label}
                          value={getAutocompleteValue(colorOptions, sideStoneDraft.color, true)}
                          onChange={(event, newValue: any) => {
                            setSideStoneDraft((prev) => ({ ...prev, color: newValue ? newValue.key : "" }));
                          }}
                          renderInput={(params) => <CustomTextField {...params} variant="outlined" placeholder="Select" style={{ background: '#fff' }} />}
                          ListboxProps={{ style: { maxHeight: 280, fontSize: '0.85rem' } }}
                        />
                      </div>

                      <div>
                        <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', color: '#999', textTransform: 'uppercase', marginBottom: '0.4rem' }}>CLARITY <span style={{ color: '#d92b3c' }}>*</span></div>
                        <Autocomplete
                          size="small"
                          options={clarityOptions}
                          getOptionLabel={(option: any) => option.label}
                          value={getAutocompleteValue(clarityOptions, sideStoneDraft.clarity)}
                          onChange={(event, newValue: any) => {
                            setSideStoneDraft((prev) => ({ ...prev, clarity: newValue ? newValue.key : "" }));
                          }}
                          renderInput={(params) => <CustomTextField {...params} variant="outlined" placeholder="Select" style={{ background: '#fff' }} />}
                          ListboxProps={{ style: { maxHeight: 280, fontSize: '0.85rem' } }}
                        />
                      </div>

                      <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', color: '#999', textTransform: 'uppercase', marginBottom: '0.4rem' }}>NO. OF STONES <span style={{ color: '#d92b3c' }}>*</span></div>
                        <CustomTextField aria-label="NO. OF STONES" variant="outlined" fullWidth size="small" value={sideStoneDraft.count} onChange={(e) => { const val = e.target.value; setSideStoneDraft((prev) => ({ ...prev, count: val })); }} style={{ background: '#fff' }} placeholder="e.g. 10" />
                      </div>
                    </div>

                    {draftError && (
                      <div style={{ color: '#d92b3c', fontSize: '0.75rem', fontWeight: 600, marginTop: '1rem', letterSpacing: '0.5px' }}>
                        {draftError}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', alignItems: 'center' }}>
                      <button type="button" onClick={saveSideStoneDraft} style={{ border: 'none', background: '#d92b3c', color: '#fff', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '1px', borderRadius: '24px', padding: '0.75rem 1.5rem', cursor: 'pointer' }}>Save stone</button>
                      <button type="button" onClick={() => { setIsAddingNew(false); setEditingIndex(null); setDraftError(null); }} style={{ background: 'transparent', border: 'none', color: '#d92b3c', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '1px', cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                )}

                {(sideStonesList.length > 0 || isAddingNew) && (
                  <div style={{ display: 'flex', alignItems: 'center', padding: '1rem', border: '1px solid #f0f0f0', borderRadius: '8px', background: '#fafafa', color: '#999', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', gap: '2rem', marginBottom: '1.5rem' }}>
                    <div>GROUPS <div style={{ color: '#1A1A1A', fontSize: '0.8rem', marginTop: '0.2rem' }}>{sideStonesList.length}</div></div>
                    <div>TOTAL STONES <div style={{ color: '#1A1A1A', fontSize: '0.8rem', marginTop: '0.2rem' }}>{sideStonesList.reduce((sum: number, s: any) => sum + (Number(s.count) || 0), 0)}</div></div>
                    <div>TOTAL CARAT <div style={{ color: '#1A1A1A', fontSize: '0.8rem', marginTop: '0.2rem' }}>{sideStonesList.reduce((sum: number, s: any) => sum + (Number(s.carat) || 0), 0).toFixed(3)} ct</div></div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {!isAddingNew ? (
                    <button type="button" onClick={openAddModal} style={{ border: '1.5px dashed #d92b3c', background: '#fff', color: '#d92b3c', padding: '0.65rem 1.25rem', borderRadius: '8px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontSize: '0.75rem' }}>
                      + ADD SIDE STONE
                    </button>
                  ) : <div></div>}
                  <button type="button" onClick={() => setSideStoneModalOpen(false)} style={{ border: 'none', background: '#d92b3c', color: '#fff', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', borderRadius: '24px', padding: '0.75rem 1.75rem', cursor: 'pointer' }}>
                    DONE
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Grid>

      {/* Origin Section */}
      <Grid item xs={12}>
        <div
          onClick={() => {
            if (!isSubmitting) {
              setAiData((prev: any) => ({
                ...prev,
                originConfirmed: !prev?.originConfirmed,
              }));
            }
          }}
          style={{
            background: '#FAFAFA',
            border: validationErrors.includes('Origin') && !aiData?.originConfirmed ? '1px solid #d92b3c' : '1px solid #EAEAEA',
            borderRadius: '12px',
            padding: '1rem 1.25rem',
            marginBottom: '1rem',
            cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', flexDirection: isMobile ? 'column' : 'row', gap: '1rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={aiData?.originConfirmed || false}
                    disabled={isSubmitting}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const newOriginConfirmed = e.target.checked;
                      setAiData((prev: any) => ({
                        ...prev,
                        originConfirmed: newOriginConfirmed,
                        ...(newOriginConfirmed ? {} : { originType: "" })
                      }));
                    }}
                    style={{ color: '#d92b3c', padding: '4px', marginRight: '8px' }}
                  />
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, letterSpacing: '1.5px', color: '#1A1A1A', textTransform: 'uppercase' }}>
                    ORIGIN <span style={{ color: '#d92b3c' }}>*</span>
                  </span>
                </div>
                {isMobile && (
                  <div style={{ fontSize: '0.85rem', lineHeight: '1.5', color: '#333', paddingLeft: '2.5rem', marginTop: '0.25rem' }}>
                    I confirm the origin of the centre and side stones above.
                  </div>
                )}
              </div>

              <div style={{ width: '150px', marginLeft: isMobile ? '2.5rem' : 'auto' }} onClick={(e) => e.stopPropagation()}>
              <FormControl variant="outlined" size="small" fullWidth>
                <Select
                  value={aiData?.originType || ""}
                  displayEmpty
                  onChange={(e) => {
                    const value = e.target.value;
                    setAiData((prev: any) => ({ ...prev, originType: value as string }));
                  }}
                  disabled={!aiData?.originConfirmed || isSubmitting}
                  style={{ background: '#fff', fontSize: '0.85rem' }}
                >
                  <MenuItem value="" disabled style={{ fontSize: '0.85rem' }}>Select origin</MenuItem>
                  <MenuItem value="Natural" style={{ fontSize: '0.85rem' }}>Natural</MenuItem>
                  <MenuItem value="Lab Grown" style={{ fontSize: '0.85rem' }}>Lab Grown</MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>
          {!isMobile && (
            <div style={{ fontSize: '0.85rem', lineHeight: '1.5', color: '#333', paddingLeft: '2.5rem', marginTop: '0.5rem' }}>
              I confirm the origin of the centre and side stones above.
            </div>
          )}
          {validationErrors.includes('Origin') && !aiData?.originConfirmed && (
            <span style={{ color: '#d92b3c', fontSize: '0.75rem', marginTop: '0.5rem', display: 'block', fontWeight: 500, paddingLeft: '2.5rem' }}>
              * Origin confirmation is mandatory.
            </span>
          )}
          </div>
        </div>
      </Grid>
    </Grid>
  );
};

export default WizardStep2AIRead;
